import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';

import ErrorBoundary from '../../error-boundary';
import GoToNextModalHotkey from '../../hotkeys/go-to-next-modal';
import GoToPreviousModalHotkey from '../../hotkeys/go-to-previous-modal';
import LinkedModalNavigationButton from '../../shared/linked-modal-navigation-button';

import { ReturnDetailsModalContentContainer } from './return-details-modal-content/return-details-modal-content-container';

import style from './return-details-modal.module.less';

export const ReturnDetailsModal = ({
  nextReturnDetailsUrl = null,
  onClose,
  previousReturnDetailsUrl = null,
  fulfillmentId,
  returnDetailModalId,
}) => {
  const [nextReturnUrl, setNextReturnUrl] = useState(nextReturnDetailsUrl);
  const [previousReturnUrl, setPreviousReturnUrl] = useState(previousReturnDetailsUrl);
  const showArrows = nextReturnUrl || previousReturnUrl;

  const history = useHistory();
  const navigateTo = useCallback((href) => history.push(href), [history]);

  // If these urls suddenly change as a result of a neighboring record changing, we
  // want to update it in state to reflect the new neighbor(s), but if they change
  // because this item changes status and is no longer in the current filtered view,
  // we want to keep memory of the previous values so that the user can still arrow
  // to the next or previous record in the grid, without having to close and reopen.
  if (nextReturnDetailsUrl && nextReturnDetailsUrl !== nextReturnUrl) {
    setNextReturnUrl(nextReturnDetailsUrl);
  }

  if (previousReturnDetailsUrl && previousReturnDetailsUrl !== previousReturnUrl) {
    setPreviousReturnUrl(previousReturnDetailsUrl);
  }

  return (
    <div className={style['return-details-wrapper']}>
      <div className={style['return-details-wrapper-background']} data-testid='return-details-wrapper' onClick={onClose} />
      {showArrows && (
        <LinkedModalNavigationButton
          arrowDirection='left'
          className={style['return-details-arrow-previous']}
          disabled={!previousReturnUrl}
          hotkey={GoToPreviousModalHotkey}
          href={previousReturnUrl}
          navigateTo={navigateTo}
        />
      )}
      <Modal className={style['return-details-modal']}>
        <ModalBody className={style['return-details-modal-body']}>
          <ErrorBoundary errorMessageId='returnDetails'>
            <ReturnDetailsModalContentContainer closeModal={onClose} returnDetailModalId={returnDetailModalId} returnId={fulfillmentId} />
          </ErrorBoundary>
        </ModalBody>
      </Modal>
      {showArrows && (
        <LinkedModalNavigationButton
          arrowDirection='right'
          className={style['return-details-arrow-next']}
          disabled={!nextReturnUrl}
          hotkey={GoToNextModalHotkey}
          href={nextReturnUrl}
          navigateTo={navigateTo}
        />
      )}
    </div>
  );
};

ReturnDetailsModal.propTypes = {
  fulfillmentId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  previousReturnDetailsUrl: PropTypes.string,
  nextReturnDetailsUrl: PropTypes.string,
  returnDetailModalId: PropTypes.string.isRequired,
};
